import React, { useCallback, useEffect, useRef, useState } from 'react';
import Editor from '@monaco-editor/react';
import { AlertTriangle, Bot, Clock, Eye, EyeOff, Play, User } from 'lucide-react';

import { createInterviewMixer, createRecorder, pickRecorderMime } from '../interviewMixer';
import { CLIP_FLAGS, startInterviewProctor } from '../interviewProctor';
import { errorFromResponse, networkErrorMessage, readJson, safeFetch, swallowNetworkRejections } from '../portalFetch';
import { detectAdvanceCue, stripAdvanceCue } from '../advanceCues';
import { formatWaitClock, msUntilStart } from '../scheduleClock';
import { appendTranscript, coversSpoken } from '../../stt-lab/labTranscript';
import { connectLabLiveStt, meetingLiveSttUrl } from '../../stt-lab/labLiveStt';
import { createLabMic } from '../../stt-lab/labMic';
import { PAUSE_MS, stripEndStop, withFullStop } from '../../stt-lab/sentencePause';

const API = `${process.env.REACT_APP_API_URL || 'https://recruitai.pingitright.com/api'}/portal`;

function browserSpeak(text) {
  return new Promise((resolve) => {
    if (!text || !window.speechSynthesis) {
      resolve();
      return;
    }
    window.speechSynthesis.cancel();
    const utter = new SpeechSynthesisUtterance(text);
    utter.rate = 1;
    utter.onend = () => resolve();
    utter.onerror = () => resolve();
    window.speechSynthesis.speak(utter);
  });
}

async function speak(text, token, mixer) {
  if (!text) return;
  try {
    const res = await safeFetch(`${API}/meeting/${token}/interview/tts`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ text }),
    });
    if (res && res.ok && res.status !== 204) {
      const blob = await res.blob();
      if (blob.size > 40) {
        if (mixer?.playTts) {
          await mixer.playTts(blob);
          return;
        }
        const url = URL.createObjectURL(blob);
        await new Promise((resolve) => {
          const audio = new Audio(url);
          audio.onended = resolve;
          audio.onerror = resolve;
          audio.play().catch(resolve);
        });
        URL.revokeObjectURL(url);
        return;
      }
    }
  } catch (_) { /* browser TTS fallback */ }
  await browserSpeak(text);
}

const SILENCE_MS = 5000;
const MIN_ANSWER_WORDS = 1;

function wordCount(text) {
  return String(text || '').trim().split(/\s+/).filter(Boolean).length;
}

function pickMime() {
  return pickRecorderMime();
}

function cloneLiveStream(stream) {
  if (!stream) return null;
  const tracks = [...stream.getVideoTracks(), ...stream.getAudioTracks()]
    .filter((t) => t.readyState === 'live');
  if (!tracks.length) return null;
  return new MediaStream(tracks);
}

function extForMime(mime) {
  return (mime || '').includes('mp4') ? 'mp4' : 'webm';
}

function evidenceParts(initChunk, recentChunks) {
  const recent = Array.isArray(recentChunks) ? recentChunks.filter(Boolean) : [];
  if (!initChunk) return recent;
  if (recent[0] === initChunk) return recent;
  return [initChunk, ...recent.filter((chunk) => chunk !== initChunk)];
}

function waitForRecorderChunk(recorder, ms = 450) {
  if (!recorder || recorder.state !== 'recording') return Promise.resolve();
  return new Promise((resolve) => {
    const timer = setTimeout(finish, ms);
    const onData = () => finish();
    function finish() {
      clearTimeout(timer);
      recorder.removeEventListener('dataavailable', onData);
      resolve();
    }
    recorder.addEventListener('dataavailable', onData);
    try { recorder.requestData(); } catch (_) { finish(); }
  });
}

async function recordCanvasClip(video, mime) {
  if (!video || video.readyState < 2 || !video.videoWidth) return null;
  const canvas = document.createElement('canvas');
  canvas.width = Math.min(video.videoWidth || 640, 960);
  canvas.height = Math.min(video.videoHeight || 480, 540);
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;
  let stream;
  try {
    stream = canvas.captureStream(8);
  } catch (_) {
    return null;
  }
  let recorder;
  try {
    recorder = createRecorder(stream, mime);
  } catch (_) {
    stream.getTracks().forEach((t) => t.stop());
    return null;
  }
  const chunks = [];
  recorder.ondataavailable = (ev) => {
    if (ev.data && ev.data.size > 0) chunks.push(ev.data);
  };
  const draw = () => {
    try { ctx.drawImage(video, 0, 0, canvas.width, canvas.height); } catch (_) { /* ignore */ }
  };
  draw();
  const id = setInterval(draw, 120);
  try {
    recorder.start(200);
    await new Promise((resolve) => setTimeout(resolve, 2400));
    await new Promise((resolve) => {
      recorder.addEventListener('stop', resolve, { once: true });
      try {
        recorder.requestData();
        recorder.stop();
      } catch (_) {
        resolve();
      }
    });
  } catch (_) {
    return null;
  } finally {
    clearInterval(id);
    stream.getTracks().forEach((t) => t.stop());
  }
  if (!chunks.length) return null;
  return new Blob(chunks, { type: recorder.mimeType || mime || 'video/webm' });
}

export default function MeetingInterviewPanel({ token, videoEl, micOn, streamRef, micLevel = 0, onMicLevel, hasStream = false, scheduledAt = null, cameraOn = true, onProctorStatus, onInterviewComplete }) {
  const [interview, setInterview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [answer, setAnswer] = useState('');
  const [code, setCode] = useState('');
  const [language, setLanguage] = useState('python');
  const [runOutput, setRunOutput] = useState('');
  const [running, setRunning] = useState(false);
  const [interim, setInterim] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [aiSpeaking, setAiSpeaking] = useState(false);
  const [listening, setListening] = useState(false);
  const [recording, setRecording] = useState(false);
  const [sttUnsupported, setSttUnsupported] = useState(false);
  const [holdForSchedule, setHoldForSchedule] = useState(false);
  const [waitMs, setWaitMs] = useState(0);
  const [remainSec, setRemainSec] = useState(null);

  const spokenRef = useRef(new Set());
  const liveRef = useRef(null);
  const answerRef = useRef('');
  const interimRef = useRef('');
  const finalsRef = useRef('');
  const startedAtRef = useRef(Date.now());
  const questionIdRef = useRef(null);
  const submittingRef = useRef(false);
  const recorderRef = useRef(null);
  const sessionChunksRef = useRef([]);
  const clipChunksRef = useRef([]);
  const clipInitRef = useRef(null);
  const flagCooldownRef = useRef({});
  const silenceTimerRef = useRef(null);
  const pauseTimerRef = useRef(null);
  const listenWantedRef = useRef(false);
  const aiSpeakingRef = useRef(false);
  const submitRef = useRef(null);
  const needsCodeRef = useRef(false);
  const answerGateRef = useRef({ silenceMs: SILENCE_MS, minWords: MIN_ANSWER_WORDS });
  const sessionUploadedRef = useRef(false);
  const lastSentChunkCountRef = useRef(0);
  const recBusyRef = useRef(false);
  const finishOnceRef = useRef(false);
  const mimeRef = useRef('video/webm');
  const recStreamRef = useRef(null);
  const mixerRef = useRef(null);
  const recUploadChainRef = useRef(Promise.resolve());
  const lastVoiceRef = useRef(0);
  const micOnRef = useRef(micOn);
  const micLevelRef = useRef(micLevel);
  const cameraOnRef = useRef(cameraOn);
  const interviewLiveRef = useRef(false);
  const onProctorStatusRef = useRef(onProctorStatus);
  const onInterviewCompleteRef = useRef(onInterviewComplete);
  const startOnceRef = useRef(false);
  const handleHeardRef = useRef(() => {});
  const publishAnswerRef = useRef(() => {});
  const spokenNowRef = useRef(() => '');
  const handleTranscriptRef = useRef(() => {});
  const handleLiveErrorRef = useRef(() => {});
  const clearSilenceRef = useRef(() => {});
  const micSttRef = useRef(null);
  const onMicLevelRef = useRef(onMicLevel);
  onMicLevelRef.current = onMicLevel;

  useEffect(() => { answerRef.current = answer; }, [answer]);
  useEffect(() => { interimRef.current = interim; }, [interim]);
  useEffect(() => { aiSpeakingRef.current = aiSpeaking; }, [aiSpeaking]);
  useEffect(() => { micOnRef.current = micOn; }, [micOn]);
  useEffect(() => { micLevelRef.current = micLevel; }, [micLevel]);
  useEffect(() => { cameraOnRef.current = cameraOn; }, [cameraOn]);
  useEffect(() => { onProctorStatusRef.current = onProctorStatus; }, [onProctorStatus]);
  useEffect(() => { onInterviewCompleteRef.current = onInterviewComplete; }, [onInterviewComplete]);
  useEffect(() => {
    const q = interview?.current_question;
    needsCodeRef.current = Boolean(q?.needs_code);
    answerGateRef.current = { silenceMs: SILENCE_MS, minWords: MIN_ANSWER_WORDS };
  }, [interview?.current_question]);

  const interviewLive = Boolean(interview) && !interview.done && !interview.waiting && interview.status !== 'waiting';
  interviewLiveRef.current = interviewLive;

  const api = useCallback(async (path, opts = {}) => {
    let res;
    try {
      res = await fetch(`${API}/meeting/${token}${path}`, {
        method: opts.method || 'GET',
        headers: opts.body ? { 'Content-Type': 'application/json' } : undefined,
        body: opts.body ? JSON.stringify(opts.body) : undefined,
        signal: opts.signal,
      });
    } catch (err) {
      const msg = networkErrorMessage(err);
      throw new Error(msg || 'Interview request cancelled.');
    }
    const data = await readJson(res);
    if (!res.ok) {
      throw new Error(errorFromResponse(data, 'Interview request failed.'));
    }
    return data;
  }, [token]);

  const uploadBlob = useCallback(async (blob, kind, flagType = '', opts = {}) => {
    if (!blob || blob.size < 200) return false;
    const mime = blob.type || mimeRef.current || 'video/webm';
    const form = new FormData();
    form.append('file', blob, `${kind}.${extForMime(mime)}`);
    form.append('kind', kind);
    form.append('flag_type', flagType || '');
    form.append('content_type', mime);
    form.append('replace', opts.replace ? '1' : '0');
    form.append('append', opts.append ? '1' : '0');
    try {
      const res = await safeFetch(`${API}/meeting/${token}/interview/recording`, {
        method: 'POST',
        body: form,
      });
      if (!res) return false;
      if (!res.ok) return false;
      return true;
    } catch (_) {
      return false;
    }
  }, [token]);

  const captureEvidenceBlob = useCallback(async () => {
    const mime = mimeRef.current || 'video/webm';
    await waitForRecorderChunk(recorderRef.current);
    let blob = new Blob(evidenceParts(clipInitRef.current, clipChunksRef.current), { type: mime });
    if (blob.size >= 1500) return blob;
    const canvasClip = await recordCanvasClip(videoEl?.current, mime);
    if (canvasClip && canvasClip.size > blob.size) return canvasClip;
    return blob.size >= 200 ? blob : canvasClip;
  }, [videoEl]);

  const postFlag = useCallback((flagType, detail, confidence) => {
    if (!interviewLiveRef.current) return;
    const now = Date.now();
    const last = flagCooldownRef.current[flagType] || 0;
    if (now - last < 10000) return;
    flagCooldownRef.current[flagType] = now;
    const clipPromise = CLIP_FLAGS.has(flagType) ? captureEvidenceBlob() : Promise.resolve(null);
    api('/interview/flag', {
      method: 'POST',
      body: { flag_type: flagType, detail: detail || '', confidence },
    }).then(async (data) => {
      if (Array.isArray(data?.flags)) {
        setInterview((prev) => (prev ? { ...prev, flags: data.flags } : prev));
      }
      const blob = await clipPromise;
      if (blob && blob.size >= 200) {
        await uploadBlob(blob, 'clip', flagType);
      }
    }).catch(() => {});
  }, [api, captureEvidenceBlob, uploadBlob]);

  const clearSilenceTimer = useCallback(() => {
    if (silenceTimerRef.current) {
      clearTimeout(silenceTimerRef.current);
      silenceTimerRef.current = null;
    }
  }, []);

  const polish = useCallback((text) => String(text || '').replace(/\s+/g, ' ').trim(), []);

  const spokenNow = useCallback(() => (
    polish(appendTranscript(finalsRef.current, interimRef.current))
  ), [polish]);

  const publishAnswer = useCallback(() => {
    const merged = spokenNow();
    answerRef.current = merged;
    setAnswer(merged);
  }, [spokenNow]);

  const clearPauseTimer = useCallback(() => {
    if (pauseTimerRef.current) {
      clearTimeout(pauseTimerRef.current);
      pauseTimerRef.current = null;
    }
  }, []);

  const closeSentence = useCallback(() => {
    pauseTimerRef.current = null;
    const merged = polish(appendTranscript(finalsRef.current, stripEndStop(interimRef.current)));
    interimRef.current = '';
    setInterim('');
    if (merged) finalsRef.current = withFullStop(merged);
    publishAnswer();
  }, [polish, publishAnswer]);

  const armPauseStop = useCallback(() => {
    clearPauseTimer();
    pauseTimerRef.current = setTimeout(closeSentence, PAUSE_MS);
  }, [clearPauseTimer, closeSentence]);

  const handleHeard = useCallback((text) => {
    const heard = String(text || '').replace(/\s+/g, ' ').trim();
    if (!heard) return false;
    const cue = detectAdvanceCue(heard);
    if (!cue) return false;
    clearSilenceTimer();
    clearPauseTimer();
    if (cue === 'skip') {
      const kept = stripAdvanceCue(heard);
      submitRef.current?.(wordCount(kept) < 4, { forceNext: true });
      return true;
    }
    submitRef.current?.(false, { forceNext: true });
    return true;
  }, [clearPauseTimer, clearSilenceTimer]);

  const handleTranscript = useCallback((payload) => {
    if (aiSpeakingRef.current || submittingRef.current || !listenWantedRef.current) return;
    const text = stripEndStop(polish(payload.text || ''));
    if (!text) return;
    const before = spokenNowRef.current();
    armPauseStop();
    if (payload.is_final) {
      if (!coversSpoken(finalsRef.current, text)) {
        finalsRef.current = polish(appendTranscript(finalsRef.current, text));
      }
      interimRef.current = '';
      setInterim('');
    } else {
      interimRef.current = text;
      setInterim(text);
    }
    publishAnswer();
    const latest = spokenNowRef.current();
    if (wordCount(latest) > wordCount(before)) lastVoiceRef.current = Date.now();
    handleHeardRef.current(latest);
  }, [armPauseStop, polish, publishAnswer]);

  const handleLiveError = useCallback((code) => {
    if (!listenWantedRef.current) return;
    setSttUnsupported(true);
    if (code === 'deepgram_key_missing') {
      setError('Voice capture is not configured. Contact the recruiter.');
    }
  }, []);

  handleHeardRef.current = handleHeard;
  publishAnswerRef.current = publishAnswer;
  spokenNowRef.current = spokenNow;
  handleTranscriptRef.current = handleTranscript;
  handleLiveErrorRef.current = handleLiveError;
  clearSilenceRef.current = clearSilenceTimer;

  const liveSocketOpen = () => {
    const sock = liveRef.current?.socket;
    return sock && (sock.readyState === WebSocket.CONNECTING || sock.readyState === WebSocket.OPEN);
  };

  const openLiveStt = useCallback(() => {
    if (liveSocketOpen()) return;
    try { liveRef.current?.stop(); } catch (_) { /* ignore */ }
    liveRef.current = connectLabLiveStt({
      url: meetingLiveSttUrl(token),
      onReady: () => setSttUnsupported(false),
      onTranscript: (payload) => handleTranscriptRef.current(payload),
      onError: (code) => handleLiveErrorRef.current(code),
    });
  }, [token]);

  const stopListening = useCallback(() => {
    listenWantedRef.current = false;
    clearSilenceTimer();
    clearPauseTimer();
    closeSentence();
    try { liveRef.current?.stop(); } catch (_) { /* ignore */ }
    liveRef.current = null;
    try { micSttRef.current?.flush(); } catch (_) { /* ignore */ }
    setListening(false);
    setInterim('');
  }, [clearPauseTimer, clearSilenceTimer, closeSentence]);

  const startListening = useCallback(() => {
    if (!micOnRef.current || aiSpeakingRef.current) return;
    listenWantedRef.current = true;
    setListening(true);
    openLiveStt();
    micSttRef.current?.ensureRunning?.()?.catch?.(() => {});
  }, [openLiveStt]);

  useEffect(() => {
    if (!hasStream || !streamRef?.current) return undefined;
    const transcriber = createLabMic({
      getStream: () => streamRef.current,
      shouldCapture: () => (
        listenWantedRef.current
        && !aiSpeakingRef.current
        && !submittingRef.current
        && micOnRef.current
      ),
      onLevel: (level) => onMicLevelRef.current?.(level),
      onPcm: (pcm) => liveRef.current?.sendPcm(pcm),
    });
    micSttRef.current = transcriber;
    transcriber.start().catch(() => {});
    return () => {
      try { transcriber.stop(); } catch (_) { /* ignore */ }
      if (micSttRef.current === transcriber) micSttRef.current = null;
    };
  }, [hasStream, streamRef]);

  useEffect(() => () => {
    listenWantedRef.current = false;
    try { liveRef.current?.stop(); } catch (_) { /* ignore */ }
    liveRef.current = null;
    if (pauseTimerRef.current) {
      clearTimeout(pauseTimerRef.current);
      pauseTimerRef.current = null;
    }
  }, []);

  const speakCurrent = useCallback(async (data) => {
    if (!data) return;
    listenWantedRef.current = false;
    try { liveRef.current?.stop(); } catch (_) { /* ignore */ }
    liveRef.current = null;
    aiSpeakingRef.current = true;
    setAiSpeaking(true);
    setInterim('');
    try {
      try { await mixerRef.current?.ready?.(); } catch (_) { /* ignore */ }
      if (data.done || data.expired) {
        if (!spokenRef.current.has('closing')) {
          spokenRef.current.add('closing');
          const last = [...(data.transcript || [])].reverse().find((m) => m.role === 'interviewer');
          if (last?.content) await speak(last.content, token, mixerRef.current);
        }
        return;
      }
      if (data.intro && !spokenRef.current.has('intro')) {
        spokenRef.current.add('intro');
        await speak(data.intro, token, mixerRef.current);
      }
      const q = data.current_question;
      if (q && !spokenRef.current.has(q.id)) {
        spokenRef.current.add(q.id);
        await speak(q.question, token, mixerRef.current);
      }
    } finally {
      aiSpeakingRef.current = false;
      setAiSpeaking(false);
      if (data.current_question && !data.done && !data.expired) {
        listenWantedRef.current = true;
        window.setTimeout(() => {
          if (!listenWantedRef.current || aiSpeakingRef.current) return;
          startListening();
        }, 80);
      }
    }
  }, [startListening, token]);

  const speakCurrentRef = useRef(speakCurrent);
  speakCurrentRef.current = speakCurrent;

  useEffect(() => {
    if (!holdForSchedule) return undefined;
    const tick = () => setWaitMs(Math.max(0, msUntilStart(scheduledAt)));
    tick();
    const id = setInterval(tick, 250);
    return () => clearInterval(id);
  }, [holdForSchedule, scheduledAt]);

  useEffect(() => {
    if (typeof interview?.remaining_sec === 'number') setRemainSec(interview.remaining_sec);
  }, [interview?.remaining_sec, interview?.question_index]);

  useEffect(() => {
    if (!interview || interview.done) return undefined;
    const id = setInterval(() => {
      setRemainSec((s) => (s == null ? s : Math.max(0, s - 1)));
    }, 1000);
    return () => clearInterval(id);
  }, [interview, interview?.done, interview?.session_id]);

  useEffect(() => {
    if (interview?.done) return undefined;
    const keepAlive = () => {
      if (!listenWantedRef.current || aiSpeakingRef.current || submittingRef.current) return;
      if (!micOnRef.current) return;
      openLiveStt();
      micSttRef.current?.ensureRunning?.()?.catch?.(() => {});
      setListening(true);
    };
    const id = setInterval(keepAlive, 800);
    const onVis = () => {
      if (document.visibilityState !== 'visible') return;
      micSttRef.current?.resume?.();
      keepAlive();
    };
    document.addEventListener('visibilitychange', onVis);
    window.addEventListener('focus', onVis);
    return () => {
      clearInterval(id);
      document.removeEventListener('visibilitychange', onVis);
      window.removeEventListener('focus', onVis);
    };
  }, [openLiveStt, interview?.done]);

  useEffect(() => {
    if (!hasStream || !streamRef?.current) return undefined;
    const mixer = createInterviewMixer(streamRef.current);
    mixerRef.current = mixer;
    mixer?.start();
    mixer?.ready?.();
    return () => {
      if (mixerRef.current === mixer) mixerRef.current = null;
      try { mixer?.stop(); } catch (_) { /* ignore */ }
    };
  }, [hasStream, streamRef]);

  useEffect(() => {
    if (!hasStream) return undefined;
    if (startOnceRef.current) return undefined;
    let cancelled = false;
    let timer = null;

    const begin = () => {
      if (cancelled || startOnceRef.current) return;
      startOnceRef.current = true;
      setHoldForSchedule(false);
      setLoading(true);
      api('/interview/start', { method: 'POST' })
        .then((data) => {
          if (cancelled) return;
          if (data?.waiting) {
            startOnceRef.current = false;
            setHoldForSchedule(true);
            setLoading(false);
            const wait = Math.max(400, Number(data.seconds_until_start || 1) * 1000);
            timer = setTimeout(begin, wait);
            return;
          }
          if (data?.done || data?.expired || data?.status === 'completed') {
            setInterview(data);
            setLoading(false);
            aiSpeakingRef.current = false;
            setAiSpeaking(false);
            return;
          }
          aiSpeakingRef.current = true;
          setAiSpeaking(true);
          setInterview(data);
          startedAtRef.current = Date.now();
          questionIdRef.current = data.current_question?.id || null;
          setLoading(false);
          speakCurrentRef.current(data);
        })
        .catch((err) => {
          if (cancelled) return;
          const msg = networkErrorMessage(err, 'Could not start the interview.');
          if (/already complete|link has expired|expired/i.test(msg || err.message || '')) {
            startOnceRef.current = true;
            setInterview({ done: true, expired: true, status: 'completed' });
            setLoading(false);
            return;
          }
          startOnceRef.current = false;
          setError(msg || 'Could not start the interview.');
          setLoading(false);
        });
    };

    const delay = msUntilStart(scheduledAt);
    if (delay > 250) {
      setHoldForSchedule(true);
      setLoading(false);
      timer = setTimeout(begin, delay);
      const poll = setInterval(() => {
        if (msUntilStart(scheduledAt) <= 0) begin();
      }, 1000);
      const onVisible = () => {
        if (document.visibilityState === 'visible' && msUntilStart(scheduledAt) <= 0) begin();
      };
      document.addEventListener('visibilitychange', onVisible);
      return () => {
        cancelled = true;
        clearTimeout(timer);
        clearInterval(poll);
        document.removeEventListener('visibilitychange', onVisible);
        window.speechSynthesis?.cancel();
        try { liveRef.current?.stop(); } catch (_) { /* ignore */ }
      };
    }

    begin();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
      window.speechSynthesis?.cancel();
      try { liveRef.current?.stop(); } catch (_) { /* ignore */ }
    };
  }, [api, hasStream, scheduledAt]);

  useEffect(() => {
    if (!interviewLive || !micOn) {
      stopListening();
      return;
    }
    if (aiSpeaking || aiSpeakingRef.current) {
      try { liveRef.current?.stop(); } catch (_) { /* ignore */ }
      liveRef.current = null;
      setListening(false);
      return;
    }
    startListening();
  }, [aiSpeaking, interviewLive, micOn, startListening, stopListening]);

  useEffect(() => {
    if (aiSpeaking || submitting) return undefined;
    const tick = () => {
      if (
        submittingRef.current
        || aiSpeakingRef.current
        || !listenWantedRef.current
        || needsCodeRef.current
      ) return;
      if (micLevelRef.current > 12) {
        lastVoiceRef.current = Date.now();
        return;
      }
      if (!lastVoiceRef.current) return;
      const latest = spokenNowRef.current();
      if (wordCount(latest) < MIN_ANSWER_WORDS) return;
      if (Date.now() - lastVoiceRef.current >= SILENCE_MS) {
        submitRef.current?.(false, { forceNext: true });
      }
    };
    const id = setInterval(tick, 200);
    return () => clearInterval(id);
  }, [aiSpeaking, submitting]);

  const stopRecorder = useCallback(async () => {
    const recorder = recorderRef.current;
    if (!recorder || recorder.state === 'inactive') return;
    await new Promise((resolve) => {
      const done = () => resolve();
      recorder.addEventListener('stop', done, { once: true });
      try {
        recorder.requestData();
        recorder.stop();
      } catch (_) {
        resolve();
      }
    });
    recorderRef.current = null;
    setRecording(false);
  }, []);

  const flushSessionDelta = useCallback(async () => {
    if (recBusyRef.current) {
      try { await recUploadChainRef.current; } catch (_) { /* ignore */ }
    }
    const chunks = sessionChunksRef.current;
    const sent = lastSentChunkCountRef.current;
    const end = chunks.length;
    if (!end || end <= sent) return sent > 0;
    const mime = mimeRef.current || 'video/webm';
    const first = sent === 0;
    const parts = chunks.slice(sent, end);
    const blob = new Blob(parts, { type: mime });
    if (blob.size < (first ? 200 : 1)) return sent > 0;
    recBusyRef.current = true;
    const run = uploadBlob(blob, 'session', '', { replace: first, append: !first })
      .then((ok) => {
        if (ok) lastSentChunkCountRef.current = end;
        return ok;
      })
      .catch(() => false)
      .finally(() => { recBusyRef.current = false; });
    recUploadChainRef.current = run.then(() => {}).catch(() => {});
    return run;
  }, [uploadBlob]);

  const finishInterview = useCallback(async () => {
    if (finishOnceRef.current) return;
    finishOnceRef.current = true;
    interviewLiveRef.current = false;
    listenWantedRef.current = false;
    try { liveRef.current?.stop(); } catch (_) { /* ignore */ }
    liveRef.current = null;
    const started = Date.now();
    while (aiSpeakingRef.current && Date.now() - started < 15000) {
      await new Promise((resolve) => setTimeout(resolve, 200));
    }
    try { await stopRecorder(); } catch (_) { /* ignore */ }
    sessionUploadedRef.current = true;
    void flushSessionDelta();
    onInterviewCompleteRef.current?.();
  }, [flushSessionDelta, stopRecorder]);
  const finishInterviewRef = useRef(finishInterview);
  finishInterviewRef.current = finishInterview;

  useEffect(() => {
    if (!hasStream || !interviewLive || recorderRef.current) return undefined;
    if (!window.MediaRecorder) {
      return undefined;
    }
    let cancelled = false;
    const start = async () => {
      const mixer = mixerRef.current;
      try { await mixer?.ready?.(); } catch (_) { /* ignore */ }
      if (cancelled || recorderRef.current) return;
      const live = mixer?.recordStream?.()
        || cloneLiveStream(streamRef?.current)
        || streamRef?.current;
      if (!live) {
        return;
      }
      try {
        const mime = pickMime();
        mimeRef.current = mime || 'video/webm';
        const recorder = createRecorder(live, mime);
        if (cancelled) return;
        recorderRef.current = recorder;
        recStreamRef.current = live;
        sessionChunksRef.current = [];
        clipChunksRef.current = [];
        clipInitRef.current = null;
        sessionUploadedRef.current = false;
        lastSentChunkCountRef.current = 0;
        recorder.ondataavailable = (ev) => {
          if (!ev.data || ev.data.size < 1) return;
          sessionChunksRef.current.push(ev.data);
          if (!clipInitRef.current) clipInitRef.current = ev.data;
          clipChunksRef.current = [...clipChunksRef.current, ev.data].slice(-12);
        };
        recorder.onerror = () => {
          setRecording(false);
        };
        recorder.start(1000);
        setRecording(true);
      } catch (err) {
        if (!cancelled) {
          recorderRef.current = null;
        }
      }
    };
    start();
    return () => { cancelled = true; };
  }, [hasStream, interviewLive, streamRef]);

  useEffect(() => {
    if (!recording || interview?.done) return undefined;
    const tick = () => {
      try { recorderRef.current?.requestData(); } catch (_) { /* ignore */ }
      if (recBusyRef.current || sessionUploadedRef.current) return;
      void flushSessionDelta();
    };
    const id = setInterval(tick, 12000);
    const first = setTimeout(tick, 4000);
    return () => {
      clearInterval(id);
      clearTimeout(first);
    };
  }, [interview?.done, recording, flushSessionDelta]);

  useEffect(() => {
    return () => {
      if (finishOnceRef.current) return;
      stopRecorder().then(() => { void flushSessionDelta(); }).catch(() => {});
    };
  }, [flushSessionDelta, stopRecorder]);

  useEffect(() => {
    if (!interview?.done) return undefined;
    interviewLiveRef.current = false;
    listenWantedRef.current = false;
    try { liveRef.current?.stop(); } catch (_) { /* ignore */ }
    liveRef.current = null;
    const stopOverlay = swallowNetworkRejections();
    const overlayTimer = setTimeout(stopOverlay, 20000);
    finishInterview().finally(() => clearTimeout(overlayTimer));
    return undefined;
  }, [interview?.done, finishInterview]);

  useEffect(() => {
    const saveOnLeave = () => {
      const recorder = recorderRef.current;
      if (recorder && recorder.state !== 'inactive') {
        try { recorder.requestData(); recorder.stop(); } catch (_) { /* ignore */ }
      }
      if (recBusyRef.current) return;
      const chunks = sessionChunksRef.current;
      const sent = lastSentChunkCountRef.current;
      if (!chunks.length || chunks.length <= sent) return;
      const mime = mimeRef.current || 'video/webm';
      const first = sent === 0;
      const parts = first ? chunks : chunks.slice(sent);
      const blob = new Blob(parts, { type: mime });
      if (blob.size < 200 || blob.size >= 60000) return;
      const form = new FormData();
      form.append('file', blob, `session.${extForMime(mime)}`);
      form.append('kind', 'session');
      form.append('flag_type', '');
      form.append('content_type', mime);
      form.append('replace', first ? '1' : '0');
      form.append('append', first ? '0' : '1');
      safeFetch(`${API}/meeting/${token}/interview/recording`, {
        method: 'POST',
        body: form,
        keepalive: true,
      }).catch(() => {});
    };
    window.addEventListener('pagehide', saveOnLeave);
    window.addEventListener('beforeunload', saveOnLeave);
    return () => {
      window.removeEventListener('pagehide', saveOnLeave);
      window.removeEventListener('beforeunload', saveOnLeave);
    };
  }, [token]);

  useEffect(() => {
    const q = interview?.current_question;
    if (!q) return undefined;
    if (questionIdRef.current !== q.id) {
      questionIdRef.current = q.id;
      setAnswer('');
      setCode('');
      setRunOutput('');
      setInterim('');
      finalsRef.current = '';
      lastVoiceRef.current = 0;
      const lang = (q.skill || '').toLowerCase();
      if (lang.includes('javascript')) setLanguage('javascript');
      else if (lang.includes('java') && !lang.includes('javascript')) setLanguage('java');
      else if (lang.includes('c++')) setLanguage('cpp');
      else setLanguage('python');
    }
    if (!aiSpeaking) startedAtRef.current = Date.now();
    return undefined;
  }, [interview?.current_question, aiSpeaking]);

  const submit = useCallback(async (skipped = false, opts = {}) => {
    if (submittingRef.current || !interview || interview.done) return;
    submittingRef.current = true;
    listenWantedRef.current = false;
    setSubmitting(true);
    stopListening();
    window.speechSynthesis?.cancel();
    let text = skipped
      ? ''
      : spokenNow();
    text = stripAdvanceCue(text);
    let isSkipped = Boolean(skipped) && wordCount(text) < 4;
    if (isSkipped) text = '';
    const forceNext = Boolean(opts.forceNext) || isSkipped;
    const elapsed = Math.floor((Date.now() - startedAtRef.current) / 1000);
    try {
      const data = await api('/interview/answer', {
        method: 'POST',
        body: {
          answer_text: text,
          code,
          is_skipped: isSkipped,
          force_next: forceNext,
          time_taken_seconds: elapsed,
        },
      });
      if (data.done || data.expired) {
        aiSpeakingRef.current = true;
        setAiSpeaking(true);
      }
      setInterview(data);
      setAnswer('');
      setCode('');
      setInterim('');
      finalsRef.current = '';
      lastVoiceRef.current = 0;
      startedAtRef.current = Date.now();
      submittingRef.current = false;
      setSubmitting(false);
      await speakCurrent(data);
    } catch (err) {
      setError(err.message);
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  }, [api, code, interview, speakCurrent, spokenNow, stopListening]);

  useEffect(() => {
    submitRef.current = submit;
  }, [submit]);

  const runCode = async () => {
    setRunning(true);
    setRunOutput('Running…');
    try {
      const data = await api('/interview/run-code', {
        method: 'POST',
        body: { language, source: code },
      });
      setRunOutput((data.output || data.error || '(no output)').trim());
    } catch (err) {
      setRunOutput(err.message);
    } finally {
      setRunning(false);
    }
  };

  useEffect(() => {
    if (!hasStream) return undefined;
    return startInterviewProctor({
      getVideo: () => videoEl?.current || null,
      getMicLevel: () => micLevelRef.current,
      getContext: () => ({
        live: interviewLiveRef.current,
        listening: listenWantedRef.current,
        aiSpeaking: aiSpeakingRef.current,
        cameraOn: cameraOnRef.current,
        micOn: micOnRef.current,
      }),
      onFlag: postFlag,
      onStatus: (status) => onProctorStatusRef.current?.(status),
    });
  }, [hasStream, postFlag, videoEl]);

  if (holdForSchedule) {
    return (
      <div style={{ textAlign: 'center', padding: '24px 8px 8px' }}>
        <Clock size={28} color="#38bdf8" />
        <div style={{ fontSize: 12, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 0.6, marginTop: 12 }}>
          Waiting for scheduled time
        </div>
        <div style={{
          fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
          fontSize: 36, fontWeight: 800, margin: '10px 0 8px', color: '#e2e8f0',
        }}>
          {formatWaitClock(waitMs)}
        </div>
        <p style={{ color: '#94a3b8', fontSize: 13, lineHeight: 1.6, margin: 0 }}>
          Stay on this page. The AI interview will start automatically.
        </p>
      </div>
    );
  }

  if (loading) {
    return <div style={{ color: '#94a3b8', fontSize: 13 }}>Starting AI interview…</div>;
  }

  if (error && !interview) {
    return (
      <div style={{ color: '#fca5a5', fontSize: 13, display: 'flex', gap: 8 }}>
        <AlertTriangle size={16} /> {error}
      </div>
    );
  }

  const q = interview?.current_question;
  const progress = interview?.questions_total
    ? Math.min(100, Math.round(((interview.question_index || 0) / interview.questions_total) * 100))
    : 0;
  const flags = interview?.flags || [];

  if (interview?.done) {
    return (
      <div>
        <div style={{ fontSize: 12, color: '#86efac', textTransform: 'uppercase', letterSpacing: 0.6 }}>Interview complete</div>
        <h3 style={{ margin: '8px 0 10px' }}>Thank you</h3>
        <p style={{ color: '#94a3b8', fontSize: 13, lineHeight: 1.6 }}>
          Thank you. The interviewer is wrapping up.
        </p>
      </div>
    );
  }

  const spokenAnswer = answer;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 420 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, marginBottom: 10 }}>
        <div>
          <div style={{ fontSize: 12, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 0.6 }}>
            AI interview {recording ? '· REC' : ''}
          </div>
          <div style={{ fontSize: 13, color: '#cbd5e1' }}>
            {q?.stage_label || 'AI interview'}
          </div>
        </div>
        {remainSec != null && (
          <div style={{ textAlign: 'right', flexShrink: 0 }}>
            <div style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
              fontSize: 22, fontWeight: 800, color: remainSec <= 60 ? '#fca5a5' : '#e2e8f0',
              letterSpacing: 0.4,
            }}>
              <Clock size={16} color={remainSec <= 60 ? '#fca5a5' : '#38bdf8'} />
              {formatWaitClock(remainSec * 1000)}
            </div>
            <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 2 }}>
              {interview?.spoken_duration_min || 30} min
              {interview?.coding_extra_min
                ? ` + ${interview.coding_extra_min} min coding`
                : ''}
            </div>
          </div>
        )}
      </div>

      <div style={{ height: 6, background: '#1e293b', borderRadius: 99, overflow: 'hidden', marginBottom: 12 }}>
        <div style={{ width: `${progress}%`, height: '100%', background: '#38bdf8' }} />
      </div>

      <div style={{
        fontSize: 12, marginBottom: 12, color: aiSpeaking ? '#93c5fd' : listening ? '#86efac' : submitting ? '#fbbf24' : '#94a3b8',
      }}>
        {aiSpeaking ? 'AI is speaking…' : submitting ? 'Saving your answer…' : q?.needs_code
          ? 'Write the solution in the editor. Say “that’s all”, “I am done with my answer”, or “skip this question” when finished.'
          : sttUnsupported
            ? 'Voice capture is unavailable. Keep the microphone on and speak your answer.'
            : listening
              ? 'Listening — pause 5 seconds to continue, or say “that’s all”, “I am done with my answer”, or “skip this question”.'
              : 'Mic idle'}
      </div>

      {q && (
        <div style={{
          flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 10, paddingRight: 4,
        }}>
          <div style={{
            background: 'rgba(37,99,235,0.14)', border: '1px solid rgba(56,189,248,0.35)',
            borderRadius: 14, padding: '14px 16px',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
              <div style={{
                width: 22, height: 22, borderRadius: 99, display: 'flex', alignItems: 'center', justifyContent: 'center',
                background: '#1d4ed8', color: '#fff',
              }}>
                <Bot size={12} />
              </div>
              <span style={{ fontSize: 10, fontWeight: 700, color: '#7dd3fc', textTransform: 'uppercase', letterSpacing: 0.8 }}>
                Question{q.title ? ` · ${q.title}` : ''}
              </span>
            </div>
            <div style={{ fontSize: 15, lineHeight: 1.6, color: '#f1f5f9', fontWeight: 500, whiteSpace: 'pre-wrap' }}>{q.question}</div>
          </div>

          <div style={{
            background: spokenAnswer ? 'rgba(16,185,129,0.1)' : '#0f172a',
            border: `1px solid ${spokenAnswer ? 'rgba(52,211,153,0.35)' : '#1e293b'}`,
            borderRadius: 14, padding: '14px 16px', minHeight: 88,
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
              <div style={{
                width: 22, height: 22, borderRadius: 99, display: 'flex', alignItems: 'center', justifyContent: 'center',
                background: '#334155', color: '#fff',
              }}>
                <User size={12} />
              </div>
              <span style={{ fontSize: 10, fontWeight: 700, color: '#86efac', textTransform: 'uppercase', letterSpacing: 0.8 }}>
                Your answer
              </span>
            </div>
            <div style={{ fontSize: 14, lineHeight: 1.6, color: spokenAnswer ? '#e2e8f0' : '#64748b' }}>
              {spokenAnswer || (aiSpeaking
                ? 'Answer will appear here after you speak.'
                : q?.needs_code
                  ? 'Write your code in the editor below. You can explain out loud if you want.'
                  : 'Pause 5 seconds when finished, or say “that’s all”, “I am done with my answer”, or “skip this question”.')}
            </div>
          </div>
        </div>
      )}

      {q?.needs_code && (
        <div style={{ marginBottom: 8 }}>
          <div style={{ display: 'flex', gap: 8, marginBottom: 6 }}>
            <select
              value={language}
              onChange={(e) => setLanguage(e.target.value)}
              style={{ background: '#0f172a', color: '#e2e8f0', border: '1px solid #1e293b', borderRadius: 8, padding: '6px 8px' }}
            >
              <option value="python">Python</option>
              <option value="javascript">JavaScript</option>
              <option value="java">Java</option>
              <option value="cpp">C++</option>
            </select>
            <button
              type="button"
              onClick={runCode}
              disabled={running || !code.trim()}
              style={{
                padding: '6px 12px', border: 'none', borderRadius: 8, fontWeight: 700,
                background: '#065f46', color: '#fff', cursor: 'pointer',
              }}
            >
              <Play size={12} style={{ verticalAlign: '-2px' }} /> {running ? 'Running…' : 'Run'}
            </button>
          </div>
          <div style={{ height: 180, border: '1px solid #1e293b', borderRadius: 10, overflow: 'hidden' }}>
            <Editor
              height="180px"
              theme="vs-dark"
              language={language === 'cpp' ? 'cpp' : language}
              value={code}
              onChange={(v) => setCode(v || '')}
              options={{ minimap: { enabled: false }, fontSize: 12, automaticLayout: true }}
            />
          </div>
          {runOutput && (
            <pre style={{
              margin: '6px 0 0', padding: 8, borderRadius: 8, background: '#020617',
              color: '#86efac', fontSize: 11, whiteSpace: 'pre-wrap', maxHeight: 90, overflow: 'auto',
            }}>{runOutput}</pre>
          )}
        </div>
      )}

      {error && <div style={{ color: '#fca5a5', fontSize: 12, marginTop: 8 }}>{error}</div>}

      {flags.length > 0 && (
        <div style={{ marginTop: 10, fontSize: 11, color: '#fbbf24', display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {flags.slice(-4).map((f) => (
            <span key={`${f.flag_type}-${f.at}`} style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
              {f.flag_type === 'TAB_HIDDEN' ? <EyeOff size={11} /> : <Eye size={11} />}
              {f.flag_type}{f.count > 1 ? ` ×${f.count}` : ''}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
